import Image, { type StaticImageData } from 'next/image'
import clsx from 'clsx'

import { TrackedLink } from '@/components/TrackedLink'

export type TestimonialData = {
  quote: string
  name: string
  role: string
  logo: StaticImageData | string | undefined
  image?: StaticImageData | string
  href?: string
}

type TestimonialCardProps = {
  testimonial: TestimonialData
  className?: string
  /** When provided, stacks all quotes in a grid to maintain consistent height (tallest quote wins) */
  allQuotes?: { key: string; quote: string }[]
}

export function TestimonialCard({
  testimonial,
  className,
  allQuotes,
}: TestimonialCardProps) {
  return (
    <div
      className={clsx(
        'relative flex w-full max-w-5xl flex-col gap-6 overflow-hidden rounded-2xl bg-neutral-950 p-4 sm:flex-row sm:items-center sm:gap-10 sm:p-12 border border-white',
        'min-h-[520px] sm:min-h-[420px] sm:items-center sm:justify-center',
        className,
      )}
    >
      {testimonial.logo && (
        <div className="flex items-center justify-center">
          {testimonial.href ? (
            <TrackedLink
              href={testimonial.href}
              rel="noopener noreferrer"
              target="_blank"
              event="outbound"
              eventData={{
                destination: testimonial.name || testimonial.role || 'partner',
                type: 'partner',
              }}
            >
              <Image
                src={testimonial.logo}
                alt={testimonial.name || testimonial.role || 'Company logo'}
                className="h-auto w-28 object-contain sm:w-40"
                width={160}
                height={80}
                unoptimized
              />
            </TrackedLink>
          ) : (
            <Image
              src={testimonial.logo}
              alt={testimonial.name || testimonial.role || 'Company logo'}
              className="h-auto w-28 object-contain sm:w-40"
              width={160}
              height={80}
              unoptimized
            />
          )}
        </div>
      )}
      <div className="flex flex-1 flex-col gap-6">
        {allQuotes ? (
          <div className="grid min-h-[260px] items-start sm:min-h-[220px]">
            {allQuotes.map((q) => (
              <blockquote
                key={q.key}
                aria-hidden={q.quote !== testimonial.quote}
                className={clsx(
                  'col-start-1 row-start-1 transition-opacity duration-200',
                  q.quote === testimonial.quote ? 'opacity-100' : 'opacity-0',
                )}
              >
                <p className="text-base italic leading-7 tracking-wide text-white/90 sm:text-lg sm:leading-8">
                  <span aria-hidden="true" className="mr-2 not-italic text-2xl text-white/50 font-serif">
                    &ldquo;
                  </span>
                  {q.quote}
                  <span aria-hidden="true" className="ml-1 not-italic text-2xl text-white/50 font-serif">
                    &rdquo;
                  </span>
                </p>
              </blockquote>
            ))}
          </div>
        ) : (
          <blockquote>
            <p className="text-lg italic leading-7 tracking-wide text-white/90 sm:text-xl sm:leading-8">
              <span aria-hidden="true" className="mr-2 not-italic text-3xl text-white/50 font-serif">
                &ldquo;
              </span>
              {testimonial.quote}
              <span aria-hidden="true" className="ml-1 not-italic text-3xl text-white/50 font-serif">
                &rdquo;
              </span>
            </p>
          </blockquote>
        )}
        {(testimonial.name || testimonial.role) && (
          <figcaption className="min-h-12 text-sm tracking-wide text-white/80 sm:text-base">
            <span className="mr-2 text-white/40">—</span>
            {testimonial.name && (
              <span className="font-semibold text-white">{testimonial.name}</span>
            )}

            {testimonial.role && <span className="text-white/70 ml-2">{testimonial.role}</span>}
          </figcaption>
        )}
      </div>
    </div>
  )
}
